/**
 * @file Map-marker.tsx
 * @module MapMarker
 * @description Galvenais marķieru kartes komponente Karšu pārlūkam.
 *              Izveido atsevišķu OpenLayers karti ar Mapbox Pamatkarte kā fona slāni,
 *              marķieru slāni (MARKER_LAYER) un Modify interakciju ģeometriju rediģēšanai.
 *              Reģistrē EPSG:3059 (LKS-92) projekciju koordinātu transformācijām.
 *              Šo komponentu iegulst SIPR sistēmas priekšpuses serviss notikumu formās.
 */
import { Feature, Map, View } from "ol";
import { Point } from "ol/geom";
import { Modify } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import { fromLonLat, toLonLat } from "ol/proj";
import VectorSource from "ol/source/Vector";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { MapDrawControlProps } from "./controls/Map-marker-draw-controls";
import proj4 from "proj4";
import * as ol from "ol/util";
import { register } from "ol/proj/proj4";
import { getDrawStyle, getHighlightStyle } from "./controls/Draw-styles";
import { Map as MapboxMap } from "mapbox-gl";
import Layer from "ol/layer/Layer.js";
import Source from "ol/source/Source.js";
import { getLayerDef } from "./new-form/Layer-loader";
import { mapSize$ } from "./new-form/Map-marker-list-wrapper";
import { DataResponse } from "../store/Layers.repository";
import { MapMarkerLayerManager } from "./Map-marker-layer-manager";
import { OverlayWrapper } from "../map/overlay/Overlay-wrapper";
import { ClassifierComponent } from "../classifiers/Classifiers";
import LayerGroup from "ol/layer/Group";
import { EventsKey } from "ol/events";

/** Component for showing markers on map with the provided coordinates */

interface MapMarkerProps {
	setValue: Dispatch<SetStateAction<MapDrawControlProps>>;
	children?: any;
	value?: [number, number] | null; // [Lon, Lat] Changing this, changes markers location
}

export const MARKER_LAYER = new VectorLayer({
	source: new VectorSource(),
	zIndex: Infinity,
	style: (f, r) => getDrawStyle(f as Feature, r)
});

const createMap = () => {
	return new Map({
		view: new View({
			center: fromLonLat([24.1056, 56.9677]),
			zoom: 8,
			maxZoom: 19
		}),
		layers: [MARKER_LAYER]
	});
};

export const MAP = createMap();

const MODIFY = new Modify({
	source: MARKER_LAYER.getSource()!,
	style: (f, r) => getHighlightStyle(f as Feature, r)
});

export const AddProjection = () => {
	proj4.defs("EPSG:3059", "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9996 +x_0=500000 +y_0=-6000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");
	register(proj4);
};

export default function MapMarker(props: MapMarkerProps) {
	const mapElement = useRef<HTMLDivElement | null>(null);
	const dataRef = useRef<DataResponse[]>();
	const resizeObs = useRef<ResizeObserver>();

	// Create map
	useEffect(() => {
		if (!mapElement.current) return;

		MAP.setLayers([MARKER_LAYER]);

		const listeners: { eventsKey: EventsKey; target: Map | Modify }[] = [];

		getLayerDef().then(res => {
			AddProjection();

			dataRef.current = res.data;
			const baseDef = res.base.find(l => l.enabled)!;

			if (!baseDef) return;

			const mbMap = new MapboxMap({
				style: baseDef.parameters,
				attributionControl: false,
				boxZoom: false,
				container: mapElement.current!,
				doubleClickZoom: false,
				dragPan: false,
				maxZoom: 19,
				dragRotate: false,
				interactive: false,
				keyboard: false,
				pitchWithRotate: false,
				scrollZoom: false,
				touchZoomRotate: false
			});

			const mbLayer = new Layer({
				zIndex: -1000,
				render(frameState) {
					const canvas = mbMap.getCanvas();
					const viewState = frameState.viewState;

					canvas.style.display = mbLayer.getVisible() ? "block" : "none";
					canvas.style.position = "absolute";
					canvas.style.opacity = String(mbLayer.getOpacity());

					mbMap.jumpTo({
						center: toLonLat(viewState.center) as any,
						zoom: viewState.zoom - 1,
						bearing: (-viewState.rotation * 180) / Math.PI
					});

					// @ts-ignore
					if (mbMap._frame) {
						// @ts-ignore
						mbMap._frame.cancel();
						// @ts-ignore
						mbMap._frame = null;
					}
					// @ts-ignore
					mbMap._render();

					return canvas;
				},
				source: new Source({
					attributions: [
						'<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a>',
						'<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>'
					]
				})
			});

			const resizeObserver = new ResizeObserver(() => {
				mbMap.resize();
			});

			resizeObserver.observe(mapElement.current!);

			resizeObs.current = resizeObserver;

			if (window.location.search.includes("?location=")) {
				const location = decodeURI(window.location.search)
					.replace("?location=", "")
					.replaceAll("%2C", ",")
					.split(",")
					.map(s => Number(s));
				const view = MAP.getView();

				view.setCenter([location[0], location[1]]);
				view.setZoom(location[2]);

				window.history.replaceState(null, "", window.location.pathname);
			}

			MAP.addLayer(mbLayer);
			MAP.setTarget(mapElement.current!);
			if (!!MAP.getSize()) mapSize$.next(MAP.getSize()! as [number, number]);
			MAP.addInteraction(MODIFY);

			props.setValue({ layer: MARKER_LAYER, map: MAP });

			// Event listeners
			listeners.push({
				eventsKey: MODIFY.on("modifystart", event => {
					const [feature] = event.features.getArray();
					feature.set("transformedCoords", undefined);
				}),
				target: MODIFY
			});

			listeners.push({
				eventsKey: MODIFY.on("modifyend", event => {
					const [feature] = event.features.getArray();
					const f = MARKER_LAYER.getSource()!.getFeatureByUid(ol.getUid(feature));
					f?.set("updatedlr", false);
					f?.set("trigger", !f.get("trigger"));
				}),
				target: MODIFY
			});

			const highlighted: Feature[] = [];
			listeners.push({
				eventsKey: MAP.on("pointermove", e => {
					const features = MAP.getFeaturesAtPixel(e.pixel, {
						hitTolerance: 7
					}) as Feature[];
					features
						.filter(f => !highlighted.find(h => ol.getUid(h) === ol.getUid(f)))
						.forEach(f => {
							f.set("highlighted", true);
							highlighted.push(f);
						});
					highlighted
						.filter(h => !features.find(f => ol.getUid(h) === ol.getUid(f)))
						.forEach(f => {
							f.set("highlighted", false);
							const i = highlighted.findIndex(h => ol.getUid(h) === ol.getUid(f));
							if (i !== -1) highlighted.splice(i, 1);
						});
				}),
				target: MAP
			});
		});

		return () => {
			if (resizeObs.current) {
				resizeObs.current.disconnect();
			}

			listeners.forEach(l => l.target.un(l.eventsKey.type as any, l.eventsKey.listener));

			const lg = MAP.getLayers()
				.getArray()
				.find(l => l instanceof LayerGroup);

			if (lg) {
				const layers = lg.getLayersArray();
				layers.forEach(l => (l.getSource() as VectorSource).clear());
			}

			MAP.setTarget(undefined);
		};
	}, [mapElement]);

	// Add marker on prop coord change
	useEffect(() => {
		if (props.value) {
			const feature = new Feature({
				geometry: new Point(fromLonLat(props.value))
			});
			const source = MARKER_LAYER.getSource()!;
			source.clear();
			source.addFeature(feature);

			MAP.getView().fit(feature.getGeometry()?.getExtent()!, {
				padding: [100, 100, 100, 100],
				maxZoom: 12,
				duration: 500
			});
		}
	}, [props.value]);

	return (
		<div ref={mapElement} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
			{props.children}
			{MAP && dataRef.current && <MapMarkerLayerManager map={MAP} data={dataRef.current} />}
			{MAP && <OverlayWrapper map={MAP}></OverlayWrapper>}
			{MAP && <ClassifierComponent></ClassifierComponent>}
		</div>
	);
}
